'use client'

import EventCard from '@/components/_cards/event/event-card'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import LightningIcon from '@/components/icons/lightning'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useState } from 'react'

export default function UpcomingEventsCarouselClient({
  logoComp = (
    <div className="flex size-12 justify-center rounded-[5px] bg-error-500 align-middle">
      <LightningIcon className="w-5 stroke-white stroke-2" />
    </div>
  ),
  titleText = 'Upcoming',
  subText = 'Browse the upcoming races starting soon on Grid Finder.',
  viewAllHref = '/sim-racing-events',
  viewAllText = 'View all',
  events = [],
}) {
  const [carouselApi, setCarouselApi] = useState(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  useEffect(() => {
    if (!carouselApi) return

    const updateScrollability = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
    }

    updateScrollability()
    carouselApi.on('select', updateScrollability)
    carouselApi.on('reInit', updateScrollability)

    return () => {
      carouselApi.off('select', updateScrollability)
      carouselApi.off('reInit', updateScrollability)
    }
  }, [carouselApi])

  const shouldShowNavigationButtons = useMemo(
    () => events.length > 3,
    [events.length],
  )

  const handleScrollPrev = useCallback(() => {
    carouselApi?.scrollPrev()
  }, [carouselApi])

  const handleScrollNext = useCallback(() => {
    carouselApi?.scrollNext()
  }, [carouselApi])

  return (
    <div className="flex flex-col gap-6 overflow-hidden">
      <div className="screen-grid *:grid-indent">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              {logoComp}
              <h5 className="text-h-6 font-semibold desktop-xs:text-h-5">
                {titleText}
              </h5>
            </div>
            <p className="text-p-2 font-normal text-neutral-blue-200 desktop-xs:text-p-3">
              {subText}
            </p>
          </div>
          <Button
            variant="outline"
            asChild
            className="scroll-py-3! px-4! hover:border-shades-0! hover:bg-shades-0! hidden w-fit transition-all duration-300 ease-in-out desktop-xs:block"
          >
            <Link
              className="hover:text-black! transition-all ease-in-out"
              href={viewAllHref}
            >
              {viewAllText}
            </Link>
          </Button>
        </div>
      </div>

      <div className="screen-grid">
        <div className="tablet:grid-indent relative col-span-full min-w-0">
          <div className="mx-[-24px] tablet:mx-0">
            {shouldShowNavigationButtons && canScrollPrev && (
              <button
                type="button"
                onClick={handleScrollPrev}
                className="carousel-btn-bg group absolute -left-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
                aria-label="Previous"
                disabled={!canScrollPrev}
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}

            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: 'center',
                slidesToScroll: 'auto',
                containScroll: false,
                breakpoints: {
                  '(min-width: 768px)': {
                    align: 'start',
                    containScroll: 'trimSnaps',
                  },
                },
              }}
              plugins={[
                Autoplay({
                  delay: 8000,
                  stopOnMouseEnter: true,
                  stopOnInteraction: false,
                }),
              ]}
              className="w-full min-w-0"
            >
              <CarouselContent>
                {events.map((event) => (
                  <CarouselItem
                    key={event.id}
                    className="basis-[calc(100%-40px)] pl-4 last:pr-4 tablet:basis-[calc(50%-8px)] desktop-xs:basis-1/3 desktop-xs:pl-4 desktop-xs:last:pr-0"
                  >
                    <EventCard
                      event={event}
                      disablePrefetch
                      viewOnly
                      showBottomStatus
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {shouldShowNavigationButtons && canScrollNext && (
            <button
              type="button"
              onClick={handleScrollNext}
              className="carousel-btn-bg group absolute -right-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              aria-label="Next"
              disabled={!canScrollNext}
            >
              <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
              </div>
            </button>
          )}
        </div>
      </div>

      <div className="screen-grid *:grid-indent grid desktop-xs:hidden">
        <Button
          variant="outline"
          asChild
          className="scroll-py-3! px-4! hover:border-shades-0! hover:bg-shades-0! block w-full text-center transition-all duration-300 ease-in-out desktop-xs:hidden"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllHref}
          >
            {viewAllText}
          </Link>
        </Button>
      </div>
    </div>
  )
}
